/**
 * Module dependencies.
 */

import { AnimationController } from 'src/components/core/animations/animation-controller';
import { GetStaticProps, NextPage } from 'next';
import { Grid, SubGrid } from 'src/components/core/layout/grid-content';
import { Heading } from 'src/components/core/heading';
import { IconParallax } from 'src/components/core/animations/icon-parallax';
import { Offers, OffersSection } from 'src/components/sections/offers';
import { PricingPack, Service } from 'src/components/core/pricing-pack';
import { Content as PrismicContent } from '@prismicio/client';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Section } from 'src/components/core/layout/section';
import { createPrimsicClient } from 'src/prismic/client';
import { getDataFromTranslations } from 'src/core/utils/objects';
import { media } from '@untile/react-core/styles/media';
import { motion, useInView } from 'framer-motion';
import { normalizePrismicSEO } from 'src/prismic/utils';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { withSettings } from 'src/hocs/with-settings';
import benefitsIcon from 'src/assets/svgs/benefits.svg';
import carIcon from 'src/assets/svgs/car.svg';
import styled from 'styled-components';
import verificar360Icon from 'src/assets/svgs/verificar360.svg';

/**
 * `Props` type.
 */

type Props = {
  data: PrismicContent.PriceDocumentData;
};

/**
 * `StyledSection` styled component.
 */

const StyledSection = styled(Section)`
  --section-padding-bottom: var(--space-sm);

  ${media.min.md`
    --section-padding-bottom: var(--section-space-xl);
    --section-padding-top: var(--section-space-xl);
  `}
`;

/**
 * `StyledSubGrid` styled component.
 */

const StyledSubGrid = styled(SubGrid)`
  ${media.min.md`
    align-items: center;
    column-gap: var(--space-xxs);
    display: grid;
    grid-template-columns: repeat(2, 1fr);
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  ${media.min.md`
    max-width: 498px;
    padding: 0;
  `}
`;

/**
 * `IconsGrid` styled component.
 */

const IconsGrid = styled(motion.div)`
  bottom: 0;
  column-gap: var(--space-xxs);
  display: grid;
  grid-template-areas: '. . icon3' '. icon2 .' ' icon1 . .';
  grid-template-columns: repeat(3, 1fr);
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: -1;

  ${media.min.md`
    grid-template-areas: '. . . icon1 . icon2 icon3 .';
    grid-template-columns: repeat(8, 1fr);
    padding-top: var(--section-space-xl);
  `}
`;

/**
 * `Pricing` page.
 */

const Pricing: NextPage<Props> = ({ data }) => {
  const { t } = useTranslation();

  const { data: batteryScanServices } = getDataFromTranslations<Service>('prices:sections.batteryScan.services', t);
  const { data: extrasServices } = getDataFromTranslations<Service>('prices:sections.extras.services', t);
  const { data: offers } = getDataFromTranslations<Offers>('prices:sections.offers.list', t);
  const { data: verificar360Services } = getDataFromTranslations<Service>('prices:sections.verificar360.services', t);

  const iconsGridRef = useRef(null);
  const isInView = useInView(iconsGridRef, { once: false });

  return (
    <>
      <SEO {...normalizePrismicSEO(data)} />

      <AnimationController staggerMobile>
        <StyledSection hasNavbarOffset style={{ overflow: 'hidden' }}>
          <Grid>
            <StyledSubGrid>
              <Content>
                <Heading label={t('prices:title')} offset staggerMobile style={{ marginBottom: 'var(--space-ms)' }} />

                <Heading
                  asDescription={'p'}
                  asTitle={'h2'}
                  description={t('prices:sections.verificar360.description')}
                  icon={verificar360Icon}
                  offset
                  staggerMobile
                  title={t('prices:sections.verificar360.name')}
                />
              </Content>

              <PricingPack
                services={[{ ...verificar360Services[0], hidePackHeader: true, hideServiceHeader: true }]}
                staggerMobile
              />
            </StyledSubGrid>
          </Grid>

          <IconsGrid ref={iconsGridRef}>
            <IconParallax
              baseVelocity={0.5}
              icon={carIcon}
              isInView={isInView}
              name={t('common:icons.transmission')}
              style={{
                alignSelf: 'end',
                gridArea: 'icon1',
                justifySelf: 'end'
              }}
            />

            <IconParallax
              icon={carIcon}
              isInView={isInView}
              name={t('common:icons.transmission')}
              style={{
                gridArea: 'icon2',
                marginTop: '-38px'
              }}
            />

            <IconParallax
              baseVelocity={3}
              icon={carIcon}
              isInView={isInView}
              name={t('common:icons.eletric')}
              style={{
                gridArea: 'icon3',
                scale: 1.5
              }}
            />
          </IconsGrid>
        </StyledSection>
      </AnimationController>

      <Section>
        <Grid>
          <StyledSubGrid>
            <Content style={{ alignSelf: 'stretch' }}>
              <AnimationController style={{ height: '100%' }}>
                <Heading
                  asDescription={'p'}
                  asTitle={'h2'}
                  description={t('prices:sections.extras.description')}
                  descriptionTheme={'slate300'}
                  icon={benefitsIcon}
                  offset
                  style={{
                    marginBottom: 'var(--space-sm)',
                    position: 'sticky',
                    top: 'calc(var(--navbar-height) + var(--section-space))'
                  }}
                  title={t('prices:sections.extras.name')}
                  titleSize={28}
                />
              </AnimationController>
            </Content>

            <div>
              <PricingPack services={[{ ...extrasServices[0], hideServiceHeader: true }]} triggerInView />
            </div>
          </StyledSubGrid>
        </Grid>
      </Section>

      <AnimationController>
        <StyledSection style={{ background: 'var(--gradient-tertiary--0-deg)' }}>
          <Grid>
            <StyledSubGrid>
              <Content>
                <Heading
                  asDescription={'p'}
                  asTitle={'h2'}
                  description={t('prices:sections.batteryScan.description')}
                  descriptionTheme={'slate300'}
                  icon={verificar360Icon}
                  offset
                  style={{ marginBottom: 'var(--space-ms)' }}
                  title={t('prices:sections.batteryScan.name')}
                />
              </Content>

              <PricingPack services={batteryScanServices} />
            </StyledSubGrid>
          </Grid>
        </StyledSection>
      </AnimationController>

      {offers.length > 0 && <OffersSection offers={offers} />}
    </>
  );
};

/**
 * Export `getStaticProps`.
 */

export const getStaticProps: GetStaticProps<SSGProps> = withSettings<any>(async ({ locale, previewData }) => {
  const client = createPrimsicClient({ previewData });
  const { data } = await client.getSingle('price');

  return {
    props: {
      ...(await serverSideTranslations(locale as string, ['common', 'prices'])),
      data
    },
    revalidate: 60
  };
});

/**
 * Export `Pricing` page.
 */

export default Pricing;
